import { useEffect, useRef } from "react";

/**
 * Unsaved-changes guard. A screen with edits that are not saved yet calls
 * useUnsavedChanges(true, "…"); leaving the page in the app (the shell's
 * go() asks confirmLeave()) or closing the tab then asks first. Autosaving
 * screens mark themselves dirty only while a save is pending or failed.
 */
const dirty = new Map<symbol, string>();

export const DEFAULT_LEAVE_MESSAGE = "You have changes that are not saved. Leave without saving?";

export function useUnsavedChanges(isDirty: boolean, message = DEFAULT_LEAVE_MESSAGE) {
  const key = useRef(Symbol("unsaved"));
  useEffect(() => {
    const k = key.current;
    if (!isDirty) {
      dirty.delete(k);
      return;
    }
    dirty.set(k, message);
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      // Older browsers show their own text but need a value set.
      e.returnValue = message;
      return message;
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => {
      dirty.delete(k);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [isDirty, message]);
}

/** Whether any screen has unsaved changes. */
export const hasUnsavedChanges = () => dirty.size > 0;

/**
 * Asks before leaving when something is unsaved; true when it is fine to
 * leave. On "leave", the guards are cleared (the screens unmount next).
 */
export function confirmLeave(ask: (message: string) => boolean = (m) => window.confirm(m)): boolean {
  if (!dirty.size) return true;
  const message = [...dirty.values()][0];
  if (!ask(message)) return false;
  dirty.clear();
  return true;
}
